import { signal, useSignalEffect } from '@preact/signals';
import { io } from 'socket.io-client';

import ChatBotNavChatbot from './components/chatBotNavChatbot.jsx';
import ChatHeaderChatbot from './components/chatHeaderChatbot.jsx';
import ChatInputFormChatbot from './components/chatInputFormChatbot.jsx';
import HistoryChatbot from './components/historyChatbot.jsx';
import './style.scss';
import { generateUuidChatbot } from './utils/uuid';

import clsx from 'clsx';
import { render } from 'preact';
import fallbackFeatureFlag from './assets/featureFlag.json';
import DropdownChatbot from './components/dropDownChatbot';
import RecommenderSlide from './components/recommenderWelcomeChatbot.jsx';
import ScreenInputLinkedinChatbot from './components/screenInputLinkedinChatbot.jsx';
import { getTokenUserFromCookieChatbot } from './utils/chatbotGetToken.js';
import {
  CHATBOT_CHAT_MSJ_FROM_BOT,
  CHATBOT_CHAT_MSJ_FROM_CLIENT,
  CHATBOT_DEFAULT_WELCOME,
  CHATBOT_ERROR_EVENT,
  CHATBOT_MODE_CHATBOT,
  CHATBOT_MODE_RECOMMENDER,
  CHATBOT_OUTSIDE_AUDIO,
  CHATBOT_OUTSIDE_MESSAGE,
  CHATBOT_SOCKET_CONNECT,
  CHATBOT_SOCKET_DISCONNECT,
  CHATBOT_SOCKET_MESSAGE,
  CHATBOT_TOKEN_EVENT,
  CHATBOT_TOKEN_STRING,
  CHATBOT_TOKEN_USER,
  CHATBOT_WELCOME_NAVIGATION,
  CHATBOT_WHATSAPP_MESSAGE
} from './utils/constants';
import { detectOSChatbot } from './utils/detectOsAndBrowser.js';
import { getBotStatus } from './utils/getBotStatus.js';
import { getPortalId } from './utils/getChatbotEnable';
import { getFeatureFlagsChatbot } from './utils/getFeatureFlags.js';
import { setupCloseDetection } from './utils/getRecaptchaToken.js';
import { loadGoogleAnalytics } from './utils/loadAnalytics.js';
import { openCallChatbot } from './utils/openCall.js';
import * as UrlUtils from './utils/urlUtils.js';

// Verbose logging flag
const verboseChatbot = import.meta.env.VITE_APP_VERBOSE === 'true';

window.sessionStorage.removeItem(CHATBOT_TOKEN_STRING);
const isOnline = true;
let socketChatbot = null;
let socketChatbotUuid = generateUuidChatbot();
const connectedChatbotState = signal(false);
const isLoadingStateDictChatbot = signal({
  [CHATBOT_MODE_CHATBOT]: false,
  [CHATBOT_MODE_RECOMMENDER]: false,
});
const dictMessengersStateChatbot = signal({
  [CHATBOT_MODE_CHATBOT]: [],
  [CHATBOT_MODE_RECOMMENDER]: [],
});
const socketChatbotToken = signal(null);
const openChatbotCount = signal(0);
const isOpenChatbot = signal(false);
const inputValueStateChatbot = signal('');
const isFirstRenderChatbot = signal(true);
const configFeatureFlagChatbot = signal(fallbackFeatureFlag);
const navigationStateChatbot = signal(0);
const welcomeNavigationStateChatbot = signal(parseInt(localStorage.getItem(CHATBOT_WELCOME_NAVIGATION)) || 0);
const modeChatbot = signal('chatbot');
const detectedOsStateChatbot = signal('');
const tokenChatbot = signal(null);
const chatbotStatusIsOnline = signal(true);
const recommenderStatusIsOnline = signal(true);
const portalIdChatbot = signal(null);
const chatbotEnableSignal = signal(null);
const userDataChatbot = signal(null);
const audioTimeoutRef = signal(null); // Reference to store audio timeout
const urlReference = import.meta.env.VITE_APP_CUSTOM_URL || window.location.href;
const urlContainsPanel = urlReference.includes('/panel/');

const urlParams = new URLSearchParams(window.location.search);
const valueQueryParam = urlParams.get('chatbot-mode');
if (valueQueryParam === 'recommender') {
  navigationStateChatbot.value = 1;
  isOpenChatbot.value = true;
} else if (valueQueryParam === 'chatbot') {
  navigationStateChatbot.value = 0;
  isOpenChatbot.value = true;
}

export function App() {
  getBotStatus(chatbotStatusIsOnline, recommenderStatusIsOnline);

  // Add event listener for chatbot-open a custom event
  useSignalEffect(() => {
    const handleChatbotOpen = (event) => {
      if (event.detail && event.detail.open) {
        // Store user data if provided
        if (event.detail.userData) {
          userDataChatbot.value = event.detail.userData;
        }

        // Open the chatbot
        if (!isOpenChatbot.value) {
          isFirstRenderChatbot.value = false;
          isOpenChatbot.value = true;
          openChatbotCount.value += 1;
          detectedOsStateChatbot.value = detectOSChatbot();

          try {
            const userId = userDataChatbot.value?.id || getTokenUserFromCookieChatbot();
            // @ts-ignore
            if (window.dataLayer) {
              const courseName = UrlUtils.getCourseNameFromUrl();
              // @ts-ignore
              window.dataLayer.push({
                'event': 'chatbot_open',
                'chatbot_user_id': userId || 'anonymous',
                'chatbot_id': socketChatbotUuid,
                'chat_count': openChatbotCount.value,
                'item_name': courseName
              });
            }
          } catch (error) {
            console.error('Error tracking chatbot open event:', error);
          }

          if (detectedOsStateChatbot.value === 'android' || detectedOsStateChatbot.value === 'ios') {
            document.documentElement.classList.add('bot-utn-no-scroll');
            document.documentElement.classList.remove('bot-utn-scroll');
            document.body.classList.add('bot-utn-no-scroll');
            document.body.classList.remove('bot-utn-scroll');
          }

          if (openChatbotCount.value === 1) {
            // Check if socket is initialized before accessing its properties
            if (socketChatbot && socketChatbot.io && socketChatbot.io.opts && socketChatbot.io.opts.query) {
              openCallChatbot(socketChatbot.io.opts.query.uuid);
            } else {
              console.warn('Socket not initialized yet, using fallback UUID');
              openCallChatbot(socketChatbotUuid);
            }
          }
        }
      }
    };

    // Add an event listener to the host element
    const embeddedComponent = document.getElementById('embedded-component');
    if (embeddedComponent) {
      embeddedComponent.addEventListener('chatbot-open', handleChatbotOpen);

      // Return cleanup function
      return () => {
        embeddedComponent.removeEventListener('chatbot-open', handleChatbotOpen);
      };
    }
  });

  const clearAudioTimeout = () => {
    if (audioTimeoutRef.value) {
      clearTimeout(audioTimeoutRef.value);
      audioTimeoutRef.value = null;
    }
  };

  const getSocketChatbot = (autoConnect = false) => {
    const token = window.sessionStorage.getItem(CHATBOT_TOKEN_STRING);
    const config = {
      // TODO: uncomment in future with feature multiple tab and clean conversation
      // query: { token: token, uuid: socketUuid },
      query: { token: isFirstRenderChatbot.value ? null : token, uuid: socketChatbotUuid },
      transports: ['websocket', 'polling'], // Add polling as fallback
      autoConnect: false,
      timeout: 10000, // 10 second timeout
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      maxReconnectionAttempts: 5
    };
    return io(import.meta.env.VITE_APP_SOCKET_URL, config);
  };

  const addAudioToListChatbot = ({ from, audio, typeMode = modeChatbot.value }, id = null) => {
    let currentMessageState = dictMessengersStateChatbot.value[typeMode];
    if (currentMessageState.length) {
      const lastElement = currentMessageState[currentMessageState.length - 1];
      if (lastElement.from === from && lastElement.audio === audio) {
        return;
      }
    }
    const newMessage = {
      id: id || -currentMessageState.length,
      from,
      message: null,
      audio,
      score: 0,
      createdAt: Date.now(),
    };
    currentMessageState = [...currentMessageState, newMessage];
    dictMessengersStateChatbot.value[typeMode] = currentMessageState;
    dictMessengersStateChatbot.value = { ...dictMessengersStateChatbot.value };
  };

  const addMessageToListChatbot = ({ from, message, typeMode = modeChatbot.value, audio = null }, id = null) => {
    // Prevent duplicated messages
    let currentMessageState = dictMessengersStateChatbot.value[typeMode];
    if (currentMessageState.length) {
      const lastElement = currentMessageState[currentMessageState.length - 1];
      if (lastElement.from === from && lastElement.message === message) {
        return;
      }
    }
    // add new messages
    const newMessage = {
      id: id || -currentMessageState.length,
      from,
      message,
      audio,
      score: 0,
      createdAt: Date.now(),
    };
    currentMessageState = [...currentMessageState, newMessage];
    dictMessengersStateChatbot.value[typeMode] = currentMessageState;
    dictMessengersStateChatbot.value = {...dictMessengersStateChatbot.value};
  };

  // Send the initial welcome only if there are no messages for the channel.
  const sendInitialWelcomeIfNeeded = (welcomeMessage, channel) => {
    const currentMessages = dictMessengersStateChatbot.value[channel];
    if (!Array.isArray(currentMessages) || currentMessages.length > 0) {
      return;
    }
    addMessageToListChatbot({
      from: 'bot',
      message: welcomeMessage,
      typeMode: channel,
    }, -1);
  };

  const sendMessageChatbot = ({from, message, audio = null, typeMode = modeChatbot.value}) => {
    const tokenUser = getTokenUserFromCookieChatbot();
    if (tokenUser) {
      tokenChatbot.value = tokenUser;
    }

    if (from === CHATBOT_CHAT_MSJ_FROM_CLIENT) {
      const recommenderIsOffline = modeChatbot.value === CHATBOT_MODE_RECOMMENDER && !recommenderStatusIsOnline.value;
      const chatbotIsOffline = modeChatbot.value === CHATBOT_MODE_CHATBOT && !chatbotStatusIsOnline.value;
      if (!connectedChatbotState.value || chatbotIsOffline || recommenderIsOffline ) {
        addMessageToListChatbot({ from: CHATBOT_CHAT_MSJ_FROM_BOT, message: CHATBOT_WHATSAPP_MESSAGE, typeMode: typeMode, audio });
        return;
      }

      // Set loading state for audio processing
      if (audio) {
        isLoadingStateDictChatbot.value[modeChatbot.value] = true;
        
        // Clear any existing timeout first
        clearAudioTimeout();
        
        // Set timeout for audio processing (3 minute)
        const timeoutId = setTimeout(() => {
          if (isLoadingStateDictChatbot.value[modeChatbot.value]) {
            console.warn('🎤 [AUDIO] TIMEOUT - Audio processing timeout after 3 minutes');
            isLoadingStateDictChatbot.value[modeChatbot.value] = false;
            
            // Show timeout message to user
            addMessageToListChatbot({ 
              from: CHATBOT_CHAT_MSJ_FROM_BOT, 
              message: 'Lo sentimos, el procesamiento del audio está tardando más de lo esperado. Por favor, intenta de nuevo.', 
              typeMode: typeMode 
            });
          }
        }, 180000); // 3 minute timeout
        
        // Store timeout reference
        audioTimeoutRef.value = timeoutId;
        console.log('🎤 [AUDIO] Audio timeout set for 3 minutes');
      }
      if (dictMessengersStateChatbot.value[modeChatbot.value].length === 1) {
        socketChatbot.emit(CHATBOT_SOCKET_MESSAGE, {
          from: CHATBOT_CHAT_MSJ_FROM_BOT,
          message: dictMessengersStateChatbot.value[modeChatbot.value][0].message,
          url: urlReference,
          mode: typeMode,
          audio: null,
          [CHATBOT_TOKEN_USER]: tokenChatbot.value,
          userPanel: tokenChatbot.value && urlContainsPanel,
          portal: portalIdChatbot.value,
          userData: userDataChatbot.value,
        });

        addMessageToListChatbot({ from, message , audio});
        // Only set loading state if it's not audio (audio timeout is handled separately)
        if (!audio) {
          isLoadingStateDictChatbot.value[modeChatbot.value] = true;
        }

        // Track the first message sent in Google Analytics
        try {
          const userId = userDataChatbot.value?.id || tokenUser || 'anonymous';

          // @ts-ignore
          if (window.dataLayer) {
            const courseName = UrlUtils.getCourseNameFromUrl();
            // @ts-ignore
            window.dataLayer.push({
              'event': 'first_message_sent',
              'chatbot_user_id': userId,
              'chatbot_id': socketChatbotUuid,
              'message_content': message.substring(0, 50),
              'item_name': courseName
            });
          }
        } catch (error) {
          console.error('Error tracking first message event:', error);
        }

        setTimeout(() => {
          socketChatbot.emit(CHATBOT_SOCKET_MESSAGE, {
            from: CHATBOT_CHAT_MSJ_FROM_CLIENT,
            message,
            url: urlReference,
            mode: typeMode,
            audio: audio,
            [CHATBOT_TOKEN_USER]: tokenChatbot.value,
            userPanel: tokenChatbot.value && urlContainsPanel,
            portal: portalIdChatbot,
            userData: userDataChatbot.value,
          });
        }, 2000);
        return;
      }

      socketChatbot.emit(CHATBOT_SOCKET_MESSAGE, {
        from: CHATBOT_CHAT_MSJ_FROM_CLIENT,
        message,
        url: urlReference,
        mode: typeMode,
        audio: audio,
        [CHATBOT_TOKEN_USER]: tokenChatbot.value,
        userPanel: tokenChatbot.value && urlContainsPanel,
        portal: portalIdChatbot,
        userData: userDataChatbot.value,
      });
      addMessageToListChatbot({ from, message , audio});
      // Only set loading state if it's not audio (audio timeout is handled separately)
      if (!audio) {
        isLoadingStateDictChatbot.value[modeChatbot.value] = true;
      }
    } else {
      addMessageToListChatbot({ from, message, audio });
    }
  };

  useSignalEffect(() => {
    if (navigationStateChatbot.value === 0) {
      modeChatbot.value = CHATBOT_MODE_CHATBOT;
    }else if (navigationStateChatbot.value === 1) {
      modeChatbot.value = CHATBOT_MODE_RECOMMENDER;
    }
  });

  useSignalEffect(() => {
    if (!isOnline || !isOpenChatbot.value) {
      return;
    }
    
    // If this is the first time opening the chatbot, we need to connect the socket
    if (openChatbotCount.value === 1) {
      console.log('First time opening chatbot, connecting socket...');
    } else if (openChatbotCount.value > 1) {
      // For subsequent openings, just mark as connected
      connectedChatbotState.value = true;
      return;
    }
    
    // Add a small delay to allow for better error handling
    const connectionTimeout = setTimeout(() => {
      const newSocketChatbot = getSocketChatbot();
    newSocketChatbot.on(CHATBOT_SOCKET_CONNECT, () => {
      // Check if socket is initialized before accessing its properties
      const queryToken = (socketChatbot && socketChatbot.io && socketChatbot.io.opts && socketChatbot.io.opts.query) 
        ? socketChatbot.io.opts.query.token 
        : null;
      if (queryToken) {
        // TODO: convert functions in async functions and execute predicates with finally for edge cases
        getFeatureFlagsChatbot(queryToken, (response) => {
          response.recommender = true;
          configFeatureFlagChatbot.value = response;
          if (!response){
            // Show default welcome message even without feature flags
            const templateWelcome = userDataChatbot.value ? `Hola ${userDataChatbot.value?.firstName}, ¿en que puedo ayudarte?` : null;
            const initialMessageChatbot = templateWelcome || CHATBOT_DEFAULT_WELCOME;
            sendInitialWelcomeIfNeeded(initialMessageChatbot, CHATBOT_MODE_CHATBOT);
            connectedChatbotState.value = true;
            return;
          }
          const templateWelcome = userDataChatbot.value ? `Hola ${userDataChatbot.value?.firstName}, ¿en que puedo ayudarte?` : null;
          const initialMessageChatbot = templateWelcome || response.initialMessageChatbot || CHATBOT_DEFAULT_WELCOME;
          sendInitialWelcomeIfNeeded(initialMessageChatbot, CHATBOT_MODE_CHATBOT);
          connectedChatbotState.value = true;
        });
      } else {
        // No token available, show default welcome message
        console.log('No token available, showing default welcome message');
        const templateWelcome = userDataChatbot.value ? `Hola ${userDataChatbot.value?.firstName}, ¿en que puedo ayudarte?` : null;
        const initialMessageChatbot = templateWelcome || CHATBOT_DEFAULT_WELCOME;
        sendInitialWelcomeIfNeeded(initialMessageChatbot, CHATBOT_MODE_CHATBOT);
        connectedChatbotState.value = true;
      }
      // Setup before unload listener with socket instance
      setupCloseDetection(newSocketChatbot, modeChatbot.value, userDataChatbot.value, urlReference);
    });

    newSocketChatbot.on(CHATBOT_OUTSIDE_MESSAGE, ({ message, id }, channel) => {
      if (!message) {
        console.error('The message is empty');
        return;
      }
      addMessageToListChatbot({ from: CHATBOT_CHAT_MSJ_FROM_BOT, typeMode: channel, message }, id);
    });

    newSocketChatbot.on(CHATBOT_OUTSIDE_AUDIO, ({ audio, id }, channel) => {
      clearAudioTimeout(); // Clear timeout when audio response is received
      isLoadingStateDictChatbot.value[channel] = false; // Disable loading when audio response is received
      addAudioToListChatbot({ from: CHATBOT_CHAT_MSJ_FROM_BOT, typeMode: channel, audio }, id);
    });
    newSocketChatbot.on(CHATBOT_ERROR_EVENT, ({ message }, channel) => {
      console.error('Error received: ', message);
      clearAudioTimeout(); // Clear timeout on error
      isLoadingStateDictChatbot.value[channel || modeChatbot.value] = false; // Disable loading on error
    });

    newSocketChatbot.on(CHATBOT_TOKEN_EVENT, ({ accessToken }) => {
      socketChatbotToken.value = accessToken;
      window.sessionStorage.setItem(CHATBOT_TOKEN_STRING, accessToken);
      
      // Check if socket is initialized before accessing its properties
      if (socketChatbot && socketChatbot.io && socketChatbot.io.opts) {
        socketChatbot.io.opts.query = {token: accessToken, uuid: socketChatbotUuid};
        newSocketChatbot.disconnect().connect();
        // send location in re-connect
      } else {
        console.warn('Socket not initialized when receiving token event');
      }
    });

    newSocketChatbot.on(CHATBOT_SOCKET_MESSAGE, ({ message, id }, channel) => {
      if (!message) {
        console.error('The message is empty');
        return;
      }
      clearAudioTimeout(); // Clear timeout when text response is received
      isLoadingStateDictChatbot.value[channel] = false;
      addMessageToListChatbot({ from: CHATBOT_CHAT_MSJ_FROM_BOT, message, typeMode: channel }, id);
    });

    newSocketChatbot.on(CHATBOT_SOCKET_DISCONNECT, () => {
      connectedChatbotState.value = false;
      
      // Clear audio timeout on disconnection
      clearAudioTimeout();
      
      // TODO: create event with multiple tabs with a modal (use here)
    });

    newSocketChatbot.on('connect_error', (error) => {
      console.error('Error connecting to socket: ', error);
      connectedChatbotState.value = false;
      
      // Clear audio timeout on connection error
      clearAudioTimeout();
      
      // Show user-friendly error message
      addMessageToListChatbot({ from: CHATBOT_CHAT_MSJ_FROM_BOT, message: CHATBOT_WHATSAPP_MESSAGE });
    });

    newSocketChatbot.on('reconnect', (attemptNumber) => {
      console.log(`Socket reconectado después de ${attemptNumber} intentos`);
      connectedChatbotState.value = true;
    });

    newSocketChatbot.on('reconnect_error', (error) => {
      console.error('Error al reconectar socket: ', error);
      connectedChatbotState.value = false;
    });

    newSocketChatbot.on('reconnect_failed', () => {
      console.error('Falló la reconexión del socket después de todos los intentos');
      connectedChatbotState.value = false;
      
      // Clear audio timeout on failed reconnection
      clearAudioTimeout();
      
      addMessageToListChatbot({ from: CHATBOT_CHAT_MSJ_FROM_BOT, message: CHATBOT_WHATSAPP_MESSAGE });
    });

    newSocketChatbot.connect();
    socketChatbot = newSocketChatbot;
    }, 100); // Small delay to allow for better error handling
    
    // Cleanup function
    return () => {
      clearTimeout(connectionTimeout);
      if (socketChatbot) {
        socketChatbot.disconnect();
      }
    };
  });


  const handlerClick = () => {
    isFirstRenderChatbot.value = false;
    isOpenChatbot.value = !isOpenChatbot.value;
    openChatbotCount.value += 1;
    detectedOsStateChatbot.value = detectOSChatbot();
    if (isOpenChatbot.value && (detectedOsStateChatbot.value === 'android' || detectedOsStateChatbot.value === 'ios')) {
      document.documentElement.classList.add('bot-utn-no-scroll');
      document.documentElement.classList.remove('bot-utn-scroll');
      document.body.classList.add('bot-utn-no-scroll');
      document.body.classList.remove('bot-utn-scroll');
    }
    if (openChatbotCount.value === 1 && isOpenChatbot.value) {
      // Check if socket is initialized before accessing its properties
      if (socketChatbot && socketChatbot.io && socketChatbot.io.opts && socketChatbot.io.opts.query) {
        openCallChatbot(socketChatbot.io.opts.query.uuid);
      } else {
        console.warn('Socket not initialized yet, using fallback UUID');
        openCallChatbot(socketChatbotUuid);
      }
    }
  };

  const welcomeAction = (skip) => {
    if (welcomeNavigationStateChatbot.value < 2) {
      return;
    }
    let initialMessageRecommender;
    if (skip) {
      initialMessageRecommender = configFeatureFlagChatbot.value.initialMessageRecommenderSkip || CHATBOT_DEFAULT_WELCOME;
    } else {
      initialMessageRecommender = configFeatureFlagChatbot.value.initialMessageRecommender || CHATBOT_DEFAULT_WELCOME;
    }

    // Guard to avoid duplicate initial recommender welcome
    sendInitialWelcomeIfNeeded(initialMessageRecommender, CHATBOT_MODE_RECOMMENDER);
  }

  const sendNavigationChange = (skip = false) => {
    // TODO: uncomment in future with feature multiple tab and clean conversation
    // const currentState = parseInt(localStorage.getItem(WELCOME_NAVIGATION)) || 0;
    // const newState = Math.min(Math.max(currentState + 1, 0), 2);
    // TODO: remove this line in future with feature multiple tab and clean conversation
    const newState = Math.min(Math.max(welcomeNavigationStateChatbot.value + 1, 0), 2);
    // TODO: uncomment in future with feature multiple tab and clean conversation
    // localStorage.setItem(WELCOME_NAVIGATION, newState.toString());
    welcomeNavigationStateChatbot.value = newState;
    welcomeAction(skip);
  }

  const resetChatbot = (e) => {
    e.preventDefault();

    // Clear any pending audio timeout
    clearAudioTimeout();

    // Reset connection with the same uuid with different index
    const index = socketChatbotUuid.split(' - ')[1];
    socketChatbotUuid = generateUuidChatbot(socketChatbotUuid, parseInt(index) + 1);
    
    // Check if socket is initialized before accessing its properties
    if (socketChatbot && socketChatbot.io && socketChatbot.io.opts) {
      socketChatbot.io.opts.query = {token: null, uuid: socketChatbotUuid};
      // Reconnect socket with new uuid
      socketChatbot.disconnect().connect();
      openCallChatbot(socketChatbot.io.opts.query.uuid);
    } else {
      console.warn('Socket not initialized, skipping reconnection');
      openCallChatbot(socketChatbotUuid);
    }
    
    window.sessionStorage.removeItem(CHATBOT_TOKEN_STRING);

    // Reset all states
    inputValueStateChatbot.value = '';
    welcomeNavigationStateChatbot.value = 0;
    dictMessengersStateChatbot.value = {
      [CHATBOT_MODE_CHATBOT]: [],
      [CHATBOT_MODE_RECOMMENDER]: [],
    };
  };

  const backgroundIsWhite = navigationStateChatbot.value === 0 || (navigationStateChatbot.value === 1
    && welcomeNavigationStateChatbot.value === 0 || welcomeNavigationStateChatbot.value === 1);

  const chatBotBackgroundIsActive = isOpenChatbot.value && backgroundIsWhite;
  const recommenderBackgroundIsActive = isOpenChatbot.value && navigationStateChatbot.value === 1 && welcomeNavigationStateChatbot.value === 2;

  return (
    <>
      {chatBotBackgroundIsActive &&
        <div className="bot-utn-mobile-background" />
      }
      {recommenderBackgroundIsActive &&
        <div className="bot-utn-mobile-recommender-background" />
      }
      <div className="bot-utn-down-elements">
        {isOpenChatbot.value &&
          <div className={clsx(
                'bot-utn-card-chat', {
                'bot-utn-recommender-background': navigationStateChatbot.value === 1 && welcomeNavigationStateChatbot.value === 2,
              })
            }
          >
            <div
              className="bot-utn-container-content">
              <ChatHeaderChatbot modeChatbot={modeChatbot.value} welcomeNavigationStateChatbot={welcomeNavigationStateChatbot.value} resetChatbotFunc={resetChatbot} welcomeNavigationState={welcomeNavigationStateChatbot} navigationState={navigationStateChatbot} isOpen={isOpenChatbot} />
              {navigationStateChatbot.value === 0 &&
                <>
                  <HistoryChatbot modeChatbot={modeChatbot.value} navigationStateChatbot={navigationStateChatbot.value} welcomeNavigationStateChatbot={welcomeNavigationStateChatbot.value} messages={dictMessengersStateChatbot.value[modeChatbot.value]} isLoadingState={isLoadingStateDictChatbot.value[modeChatbot.value]}/>
                  <ChatInputFormChatbot sendMessage={sendMessageChatbot} isLoading={(isLoadingStateDictChatbot.value[modeChatbot.value] || !connectedChatbotState)} inputValueState={inputValueStateChatbot}/>
                </>
              }
              {navigationStateChatbot.value === 1 && welcomeNavigationStateChatbot.value === 0 &&
                <>
                  <RecommenderSlide sendNavigationChange={sendNavigationChange}/>
                </>
              }
              {navigationStateChatbot.value === 1 && welcomeNavigationStateChatbot.value === 1 &&
                <>
                  <ScreenInputLinkedinChatbot sendNavigationChange={sendNavigationChange} sendMessage={sendMessageChatbot}/>
                </>
              }
              {navigationStateChatbot.value === 1 && welcomeNavigationStateChatbot.value === 2 &&
                <>
                  <HistoryChatbot modeChatbot={modeChatbot.value} navigationStateChatbot={navigationStateChatbot.value} welcomeNavigationStateChatbot={welcomeNavigationStateChatbot.value} messages={dictMessengersStateChatbot.value[modeChatbot.value]} isLoadingState={isLoadingStateDictChatbot.value[modeChatbot.value]}/>
                  <ChatInputFormChatbot sendMessage={sendMessageChatbot} isLoading={(isLoadingStateDictChatbot.value[modeChatbot.value] || !connectedChatbotState)} inputValueState={inputValueStateChatbot}/>
                </>
              }
              <ChatBotNavChatbot navigationState={navigationStateChatbot}/>
            </div>
          </div>
        }
          <div className="bot-utn-dropdown-chatbot-div">
              <DropdownChatbot isOpenChatbot={isOpenChatbot.value} handlerClick={handlerClick} isFirstRenderChatbot={isFirstRenderChatbot.value}/>
          </div>
        </div>
      </>
        );
      }
(async function getPortalIdAndRenderChatbot() {
  // Load token if it exists
  const tokenUser = getTokenUserFromCookieChatbot();
  if (tokenUser) {
    tokenChatbot.value = tokenUser;
  }
  
  // Initialize Google Analytics
  loadGoogleAnalytics();
  
  // get portal id and render chatbot if not in panel
  await new Promise(resolve => setTimeout(resolve, 3000));
  portalIdChatbot.value = await getPortalId();
  
  const embeddedComponentShadow = document.getElementById('embedded-component').shadowRoot;
  const toRender = embeddedComponentShadow.getElementById('bot-utn-chatbot-utn-app');
  
  render(<App />, toRender);
  
  if (verboseChatbot) {
    console.info('🎉 Widget Chatbot UTN: Component successfully rendered and ready!');
  }
})();
